import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowUpRight, Plus, X, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function ApprovalRoutingTab() {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState({});

  const load = async () => {
    try { const r = await api.get("/admin/approval-routing"); setData(r.data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't load routing"); }
  };
  useEffect(() => { load(); }, []);

  const setFlag = async (flow, userId, value) => {
    try {
      await api.put(`/admin/users/${userId}/${flow.endpoint}`, { [flow.flag]: value });
      toast.success(value ? "Recipient added" : "Recipient removed");
      setAdding(a => ({ ...a, [flow.key]: "" }));
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Update failed"); }
  };

  if (!data) return <div className="opacity-50 font-display tracking-widest">LOADING…</div>;

  return (
    <div className="space-y-4" data-testid="approval-routing">
      <div>
        <h2 className="font-display font-black text-2xl">Approval routing</h2>
        <p className="opacity-60 text-sm mt-1">Who receives each approval or review request. Add or remove people here — changes take effect immediately. Admins are always included where marked.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {data.flows.map(f => {
          const currentIds = new Set(f.recipients.map(r => r.id));
          const candidates = data.users.filter(u => !currentIds.has(u.id));
          return (
            <div key={f.key} className={`card-tactical rounded-md p-4 ${f.warning ? "border-amber-500/50" : ""}`} data-testid={`routing-flow-${f.key}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display font-bold text-base">{f.title}</div>
                  <div className="text-[11px] opacity-60 mt-0.5">{f.rule || f.dynamic}</div>
                </div>
                <Link to={f.to} className="text-[10px] tracking-widest uppercase opacity-60 hover:opacity-100 hover:text-[#83B68B] inline-flex items-center gap-1 whitespace-nowrap">Open <ArrowUpRight className="w-3 h-3" /></Link>
              </div>
              {f.warning && <div className="mt-2 text-xs text-amber-400 inline-flex items-center gap-1.5" data-testid={`routing-warning-${f.key}`}><AlertTriangle className="w-3.5 h-3.5" /> {f.warning}</div>}
              {f.dynamic ? (
                <div className="mt-3 text-xs opacity-70 italic">Routed automatically: {f.dynamic}</div>
              ) : (
                <>
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`routing-recipients-${f.key}`}>
                    {f.recipients.length === 0 && <span className="text-xs opacity-50">No recipients</span>}
                    {f.recipients.map(r => (
                      <span key={r.id} className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border ${r.via === "admin" ? "border-border opacity-70" : "border-[#83B68B]/50 text-[#83B68B] bg-[#83B68B]/10"}`} title={r.email} data-testid={`routing-recipient-${f.key}-${r.id}`}>
                        {r.via === "admin" && <ShieldCheck className="w-3 h-3" />}{r.name || r.email}
                        {r.via === "admin" ? <span className="opacity-60 ml-0.5">admin</span> : (
                          <button type="button" onClick={() => setFlag(f, r.id, false)} className="ml-0.5 hover:text-red-400" title="Remove" data-testid={`routing-remove-${f.key}-${r.id}`}><X className="w-3 h-3" /></button>
                        )}
                      </span>
                    ))}
                  </div>
                  {f.flag && (
                    <div className="mt-3 flex items-center gap-2">
                      <Select value={adding[f.key] || ""} onValueChange={v => setAdding(a => ({ ...a, [f.key]: v }))}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`routing-add-select-${f.key}`}><SelectValue placeholder="Add a person…" /></SelectTrigger>
                        <SelectContent>{candidates.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email} · {u.role}</SelectItem>)}</SelectContent>
                      </Select>
                      <button type="button" disabled={!adding[f.key]} onClick={() => setFlag(f, adding[f.key], true)} data-testid={`routing-add-btn-${f.key}`}
                        className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-[#83B68B]/50 text-[#83B68B] hover:bg-[#83B68B] hover:text-white disabled:opacity-40 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
